/**
 * Copy of Data Vault › Product / Price Sync (`components/ProductPriceSync/`),
 * from the OTTO part of VentaHub's `pages/product_price_upload.tsx`: the four
 * step names and the template's column list are kept as VentaHub has them.
 */
export const productPriceSync = {
  title: 'Product / Price Sync',
  description: 'Upload prices for many OTTO SKUs at once, and follow every batch to the end.',
  steps: {
    label: 'Progress',
    store: 'Select Store',
    template: 'Download Template',
    upload: 'Upload File',
    status: 'Check Status',
    // Read after the step's name by a screen reader; the marker says it visually.
    done: '(done)',
    current: '(current step)',
    upcoming: '(to do)',
  },
  store: {
    title: 'Select store',
    label: 'Store',
    placeholder: 'Pick an OTTO store',
    empty: 'No store can take a price upload yet',
    emptyHint: 'A store appears here once it is an OTTO store you have access to.',
  },
  template: {
    title: 'Download template',
    hint: 'Columns: Sku, Current Price, New Price, Sale Start (DD-MM-YYYY), Sale End (DD-MM-YYYY), and an optional Currency (EUR when left blank).',
    download: 'Download template',
    downloading: 'Downloading…',
  },
  upload: {
    title: 'Upload file',
    hint: 'A New Price is a sale price for its Sale Start to Sale End, and must be lower than the Current Price.',
    waitingFor: 'Pick a store first',
    dryRun: 'Dry run — check the sheet and plan the push, send nothing to OTTO',
    liveTitle: 'Prices will be sent to OTTO',
    liveWarning:
      'With dry run off, every price in this sheet is sent to OTTO for {{store}} and goes live there. Leave dry run on to check the sheet first.',
    submit: 'Upload & send to OTTO',
    submitDryRun: 'Upload & check',
    uploading: 'Uploading…',
    problemsTitle: 'The sheet was not pushed. Fix these rows and upload it again:',
    problemRow: 'Row {{row}}',
    problemCell: 'Row {{row}} · {{column}}',
    accepted_one: '{{count}} SKU taken from the sheet.',
    accepted_other: '{{count}} SKUs taken from the sheet.',
    queued: 'Push queued. Its run opens here once it starts.',
    queuedDryRun: 'Dry run queued. Its run opens here once it starts.',
    viewOnly: 'You can follow this store’s price uploads. Uploading needs catalogue edit access.',
  },
  runs: {
    title: 'Processing status',
    pickStore: 'Pick a store',
    pickStoreHint: 'Its price uploads and their status appear here.',
    empty: 'No price uploads yet',
    emptyHint: 'Upload a sheet; its run appears here.',
    waiting: 'Waiting for the run to start…',
    notStarted: {
      title: 'Could not start the run',
      hint: 'The push was queued, but no run started within {{seconds}} seconds. Wait again, or quote the reference below to support.',
      retry: 'Wait again',
    },
    columns: {
      created: 'Uploaded',
      mode: 'Mode',
      status: 'Status',
      skus: 'SKUs',
      succeeded: 'Succeeded',
      failed: 'Failed',
    },
    dryRun: 'Dry run',
    live: 'Live',
    open: 'Open run from {{when}}',
  },
  run: {
    title: 'Run {{id}}',
    close: 'Close run',
    slow: 'OTTO takes 10–15 minutes to process price updates. This page checks every few seconds — you can leave and come back.',
    noCounts: 'Counts arrive once the run has sent its batches.',
    counts: {
      skus: 'SKUs',
      batches: 'Batches',
      succeeded: 'Succeeded',
      failed: 'Failed',
      unchanged: 'Unchanged',
      pending: 'Pending',
      planned: 'Planned',
    },
    batches: {
      title: 'Batches',
      empty: 'No batch sent',
      emptyHint:
        'A dry run sends nothing; a live run lists each batch of 500 here as OTTO takes it.',
      columns: {
        process: 'Process',
        state: 'State',
        total: 'Total',
        succeeded: 'Succeeded',
        failed: 'Failed',
        unchanged: 'Unchanged',
        message: 'Message',
      },
    },
    outcomes: {
      failed: 'Failed',
      pending: 'Pending',
      succeeded: 'Succeeded',
      planned: 'Planned (dry run)',
    },
    rows: {
      empty: 'No SKU outcome yet',
      emptyHint: 'Each SKU’s outcome is listed here once OTTO has answered for its batch.',
      columns: { sku: 'SKU', error: 'Why' },
      search: 'Find a SKU',
      searchPlaceholder: 'SKU contains',
      noMatch: 'No SKU in this run contains “{{sku}}”.',
      truncated: 'Only the first rows are shown; the counts above cover every SKU.',
    },
  },
} as const
